import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';

const ATTACHMENT_ALLOWED_MIME_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
const ATTACHMENT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

const MAIL_TYPES = [
  {
    id: 'algemeen',
    label: 'Algemeen',
    keys: ['email_aanhef', 'email_slotgroet'],
    variabelen: [],
  },
  {
    id: 'factuur',
    label: 'Factuur',
    keys: ['email_intro_factuur'],
    variabelen: ['naam', 'bedrag', 'factuurnummer', 'betaaldatum', 'bedrijfsnaam'],
  },
  {
    id: 'herinnering',
    label: 'Herinnering',
    keys: ['email_intro_herinnering'],
    variabelen: ['naam', 'bedrag', 'factuurnummer', 'betaaldatum', 'bedrijfsnaam'],
  },
  {
    id: 'aanmaning',
    label: 'Aanmaning',
    keys: ['email_intro_aanmaning'],
    variabelen: ['naam', 'bedrag', 'factuurnummer', 'betaaldatum', 'bedrijfsnaam'],
  },
  {
    id: 'incasso',
    label: 'Incasso',
    keys: ['email_intro_incasso'],
    variabelen: ['naam', 'bedrag', 'factuurnummer', 'betaaldatum', 'bedrijfsnaam'],
  },
  {
    id: 'welcome_prospect',
    label: 'Welkomst prospect',
    keys: ['email_welcome_prospect_subject', 'email_welcome_prospect_intro'],
    variabelen: ['naam', 'bedrijfsnaam', 'gym_url', 'app_store_url', 'play_store_url'],
  },
  {
    id: 'welcome_active',
    label: 'Welkomst actief lid',
    keys: ['email_welcome_active_subject', 'email_welcome_active_intro'],
    variabelen: ['naam', 'bedrijfsnaam', 'gym_url', 'app_store_url', 'play_store_url'],
  },
];

const ALL_TEKST_KEYS = [
  'email_aanhef', 'email_slotgroet',
  'email_intro_factuur', 'email_intro_herinnering',
  'email_intro_aanmaning', 'email_intro_incasso',
  'email_welcome_prospect_subject', 'email_welcome_prospect_intro',
  'email_welcome_active_subject', 'email_welcome_active_intro',
];

const HTML_KEYS = [
  'email_html_factuur',
  'email_html_herinnering',
  'email_html_aanmaning',
  'email_html_incasso',
];

const HTML_TABS = [
  { key: 'email_html_factuur',     label: 'HTML — Factuur' },
  { key: 'email_html_herinnering', label: 'HTML — Herinnering' },
  { key: 'email_html_aanmaning',   label: 'HTML — Aanmaning' },
  { key: 'email_html_incasso',     label: 'HTML — Incasso' },
];

const FIELD_META = {
  email_aanhef:                  { label: 'Aanhef (gedeeld)', rows: 2, hint: t('emailteksten.hintAanhef'), placeholder: 'Geachte [naam],' },
  email_slotgroet:               { label: 'Slotgroet (gedeeld)', rows: 3, hint: t('emailteksten.hintSlotgroet'), placeholder: 'Met vriendelijke groet,' },
  email_intro_factuur:           { label: 'Introductietekst', rows: 8, placeholder: 'Hierbij ontvangt u factuur [factuurnummer] van [bedrijfsnaam].' },
  email_intro_herinnering:       { label: 'Introductietekst', rows: 8, placeholder: 'Volgens onze administratie hebben we nog geen betaling ontvangen voor factuur [factuurnummer].' },
  email_intro_aanmaning:         { label: 'Introductietekst', rows: 8, placeholder: 'Dit is de laatste aanmaning voor factuur [factuurnummer].' },
  email_intro_incasso:           { label: 'Introductietekst', rows: 8, placeholder: 'Factuur [factuurnummer] is overgedragen aan een incassobureau.' },
  email_welcome_prospect_subject: { label: 'Onderwerp', rows: 1, placeholder: 'Welkom bij [bedrijfsnaam]!' },
  email_welcome_prospect_intro:  { label: 'Introductietekst', rows: 10, placeholder: 'Welkom [naam],\n\n...' },
  email_welcome_active_subject:  { label: 'Onderwerp', rows: 1, placeholder: 'Welkom als lid bij [bedrijfsnaam]!' },
  email_welcome_active_intro:    { label: 'Introductietekst', rows: 10, placeholder: 'Welkom [naam],\n\n...' },
};

function VariabelenChips({ variabelen }) {
  const { showSuccess } = useToast();
  if (!variabelen.length) return null;

  function copyVar(v) {
    navigator.clipboard.writeText(`[${v}]`).then(() => {
      showSuccess(t('emailteksten.variabelGekopieerdPrefix') + `[${v}]`);
    });
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, marginTop: 0 }}>
        {t('emailteksten.beschikbareVariabelen')}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {variabelen.map(v => (
          <button
            key={v}
            type="button"
            onClick={() => copyVar(v)}
            title={t('emailteksten.klikOmTeKopieren')}
            style={{
              background: 'var(--color-primary-light, #eff6ff)',
              color: 'var(--color-primary, #2563eb)',
              border: '1px solid var(--color-primary-border, #bfdbfe)',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 12,
              fontFamily: 'monospace',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            [{v}]
          </button>
        ))}
      </div>
      <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 6, marginBottom: 0 }}>
        {t('emailteksten.klikChipHint')}
      </p>
    </div>
  );
}

function EmailPreview({ activeTab, teksten }) {
  const introKey = MAIL_TYPES.find(type => type.id === activeTab)?.keys.find(key => key.includes('intro'));
  const subjectKey = MAIL_TYPES.find(type => type.id === activeTab)?.keys.find(key => key.includes('subject'));
  const replaceExample = (value = '') => value
    .replaceAll('[naam]', t('beheer.voorbeeldKlant'))
    .replaceAll('[bedrijfsnaam]', t('app.name'))
    .replaceAll('[bedrag]', '€ 79,95')
    .replaceAll('[factuurnummer]', '2026-0042')
    .replaceAll('[betaaldatum]', '23 augustus 2026');

  return (
    <aside className="message-preview" aria-label={t('beheer.voorbeeld')}>
      <div className="message-preview-label">{t('beheer.voorbeeld')}</div>
      <div className="message-preview-window">
        <div className="message-preview-subject">
          {replaceExample(subjectKey ? teksten[subjectKey] : '') || t('beheer.voorbeeldOnderwerp')}
        </div>
        <div className="message-preview-body">
          <p>{replaceExample(teksten.email_aanhef) || `Beste ${t('beheer.voorbeeldKlant')},`}</p>
          <p>{replaceExample(introKey ? teksten[introKey] : '') || t('beheer.voorbeeldUitleg')}</p>
          <p>{replaceExample(teksten.email_slotgroet) || t('app.name')}</p>
        </div>
      </div>
    </aside>
  );
}

const REMINDER_VARIABELEN = ['naam', 'bedrijfsnaam'];

export default function EmailtekstenPage() {
  const { role, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const isSuperadmin = role === 'superadmin';

  const [activeTab, setActiveTab] = useState('algemeen');
  const [teksten, setTeksten] = useState(() =>
    Object.fromEntries(ALL_TEKST_KEYS.map(k => [k, '']))
  );
  const [htmlTemplates, setHtmlTemplates] = useState(() =>
    Object.fromEntries(HTML_KEYS.map(k => [k, '']))
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingReminder, setSavingReminder] = useState(false);
  const [reminderMax, setReminderMax] = useState({ agreement: 0, prospect: 0 });
  const [reminderTexts, setReminderTexts] = useState({ agreement: {}, prospect: {} });
  const [reminderAttachments, setReminderAttachments] = useState({ agreement: {}, prospect: {} });
  const [uploadingAttachment, setUploadingAttachment] = useState(null);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'agreement_reminder' || hash === 'prospect_reminder') {
      setActiveTab(hash);
    }
  }, []);

  useEffect(() => {
    async function loadReminders() {
      try {
        const { data: settingsData, error: sErr } = await supabase
          .from('settings')
          .select('key, value')
          .in('key', ['agreement_reminder_max', 'prospect_reminder_max']);
        if (sErr) throw sErr;
        const maxMap = {};
        (settingsData || []).forEach(({ key, value }) => { maxMap[key] = value; });
        setReminderMax({
          agreement: parseInt(maxMap.agreement_reminder_max || '0', 10) || 0,
          prospect: parseInt(maxMap.prospect_reminder_max || '0', 10) || 0,
        });

        const { data: textRows, error: tErr } = await supabase
          .from('reminder_texts')
          .select('reminder_type, reminder_number, subject, intro');
        if (tErr) throw tErr;
        const textMap = { agreement: {}, prospect: {} };
        (textRows || []).forEach(r => {
          textMap[r.reminder_type][r.reminder_number] = { subject: r.subject || '', intro: r.intro || '' };
        });
        setReminderTexts(textMap);

        const { data: attRows, error: aErr } = await supabase
          .from('reminder_attachments')
          .select('id, reminder_type, reminder_number, storage_path, filename')
          .order('created_at', { ascending: true });
        if (aErr) throw aErr;
        const attMap = { agreement: {}, prospect: {} };
        (attRows || []).forEach(r => {
          if (!attMap[r.reminder_type][r.reminder_number]) attMap[r.reminder_type][r.reminder_number] = [];
          attMap[r.reminder_type][r.reminder_number].push(r);
        });
        setReminderAttachments(attMap);
      } catch (err) {
        showError(t('emailteksten.foutLadenHerinneringen') + err.message);
      }
    }
    loadReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setReminderTextField(type, number, field, value) {
    setReminderTexts(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [number]: { ...(prev[type][number] || { subject: '', intro: '' }), [field]: value },
      },
    }));
  }

  async function handleSaveReminderTexts(type) {
    setSavingReminder(true);
    try {
      const max = reminderMax[type];
      if (!max || max < 1) {
        showError(t('emailteksten.geenMaximumIngesteld'));
        return;
      }
      const rows = [];
      for (let i = 1; i <= max; i++) {
        const entry = reminderTexts[type][i] || { subject: '', intro: '' };
        rows.push({ reminder_type: type, reminder_number: i, subject: entry.subject || '', intro: entry.intro || '' });
      }
      // Rij-check: een upsert die bij bestaande rijen een UPDATE wordt kan door
      // RLS stil 0 rijen raken (geen fout, PostgREST meldt succes).
      const { data: saved, error } = await supabase
        .from('reminder_texts')
        .upsert(rows, { onConflict: 'reminder_type,reminder_number' })
        .select('reminder_type');
      if (error) throw error;
      if ((saved?.length ?? 0) !== rows.length) {
        showError(t('common.wijzigingNietOpgeslagen'));
        return;
      }
      showSuccess(t('emailteksten.opgeslagenHerinneringen'));
    } catch (err) {
      showError(t('emailteksten.foutOpslaanHerinneringen') + err.message);
    } finally {
      setSavingReminder(false);
    }
  }

  async function handleUploadAttachment(type, number, file) {
    // Onderdeel 4 (2026-07-27, Midden-fix — Opus-review): client-side check
    // vóór de upload — de echte, niet te omzeilen grens staat op bucket-
    // niveau (allowed_mime_types/file_size_limit, migratie 20260727210000).
    if (!ATTACHMENT_ALLOWED_MIME_TYPES.includes(file.type)) {
      showError(t('emailteksten.foutBijlageType'));
      return;
    }
    if (file.size > ATTACHMENT_MAX_SIZE_BYTES) {
      showError(t('emailteksten.foutBijlageGrootte'));
      return;
    }
    const busyKey = `${type}-${number}`;
    setUploadingAttachment(busyKey);
    try {
      const path = `${type}/${number}/${Date.now()}_${file.name}`;
      const { error: upErr } = await supabase.storage.from('reminder-attachments').upload(path, file, { upsert: false });
      if (upErr) throw upErr;
      const { data: inserted, error: insErr } = await supabase
        .from('reminder_attachments')
        .insert({ reminder_type: type, reminder_number: number, storage_path: path, filename: file.name, uploaded_by: user?.id || null })
        .select()
        .maybeSingle();
      if (insErr) throw insErr;
      if (!inserted) throw new Error('Bijlage kon niet worden geregistreerd.');
      setReminderAttachments(prev => ({
        ...prev,
        [type]: { ...prev[type], [number]: [...(prev[type][number] || []), inserted] },
      }));
      showSuccess(t('emailteksten.bijlageGeupload'));
    } catch (err) {
      showError(t('emailteksten.foutBijlageUpload') + err.message);
    } finally {
      setUploadingAttachment(null);
    }
  }

  async function handleDeleteAttachment(type, number, attachment) {
    try {
      // Volgorde omgedraaid (2026-08-15): eerst de databaserij, dán pas het
      // storage-bestand. Andersom kon het bestand al weg zijn terwijl de
      // DELETE op reminder_attachments door RLS 0 rijen raakte (géén fout) —
      // dan blijft er een bijlage-rij staan die naar een niet-bestaand bestand
      // wijst en breekt de herinneringsmail bij het versturen. Blijft het
      // storage-bestand achter, dan is dat hooguit een wees in de bucket.
      const { data: delRow, error: delErr } = await supabase
        .from('reminder_attachments')
        .delete()
        .eq('id', attachment.id)
        .select('id')
        .maybeSingle();
      if (delErr) throw delErr;
      if (!delRow) {
        showError(t('common.wijzigingNietVerwijderd'));
        return;
      }
      const { error: rmErr } = await supabase.storage.from('reminder-attachments').remove([attachment.storage_path]);
      if (rmErr) throw rmErr;
      setReminderAttachments(prev => ({
        ...prev,
        [type]: { ...prev[type], [number]: (prev[type][number] || []).filter(a => a.id !== attachment.id) },
      }));
      showSuccess(t('emailteksten.bijlageVerwijderd'));
    } catch (err) {
      showError(t('emailteksten.foutBijlageVerwijderen') + err.message);
    }
  }

  function renderReminderTab(type) {
    const max = reminderMax[type];
    const label = type === 'agreement' ? t('emailteksten.tabOvereenkomstHerinnering') : t('emailteksten.tabProspectHerinnering');
    return (
      <>
        <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, marginTop: 0 }}>{label}</h2>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 20 }}>
          {max > 0 ? t('emailteksten.herinneringPerIndexUitleg', { max }) : t('emailteksten.geenMaximumIngesteld')}
        </p>
        <VariabelenChips variabelen={REMINDER_VARIABELEN} />
        {max > 0 && (
          <form onSubmit={e => { e.preventDefault(); handleSaveReminderTexts(type); }}>
            {Array.from({ length: max }, (_, idx) => idx + 1).map(number => {
              const entry = reminderTexts[type][number] || { subject: '', intro: '' };
              const attachments = reminderAttachments[type][number] || [];
              const busy = uploadingAttachment === `${type}-${number}`;
              return (
                <div key={number} style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0, marginBottom: 12 }}>
                    {t('emailteksten.herinneringLabel', { n: number })}
                  </h3>
                  <div className="form-group">
                    <label className="form-label">{t('emailteksten.labelOnderwerpHerinnering')}</label>
                    <input
                      className="form-input"
                      value={entry.subject}
                      onChange={e => setReminderTextField(type, number, 'subject', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('emailteksten.labelIntroHerinneringIndex')}</label>
                    <textarea
                      className="form-input"
                      rows={6}
                      value={entry.intro}
                      onChange={e => setReminderTextField(type, number, 'intro', e.target.value)}
                      style={{ resize: 'vertical' }}
                    />
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('emailteksten.bijlagenTitel')}</p>
                    <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 8 }}>{t('emailteksten.bijlagenUitleg')}</p>
                    {attachments.length === 0 && (
                      <p style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{t('emailteksten.geenBijlagen')}</p>
                    )}
                    {attachments.map(att => (
                      <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 12 }}>{att.filename}</span>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{ fontSize: 11, padding: '2px 8px' }}
                          onClick={() => handleDeleteAttachment(type, number, att)}
                        >
                          {t('emailteksten.bijlageVerwijderen')}
                        </button>
                      </div>
                    ))}
                    <label
                      className="btn btn-ghost"
                      style={{ fontSize: 12, display: 'inline-block', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}
                    >
                      {busy ? t('auth.loading') : t('emailteksten.bijlageUploaden')}
                      <input
                        type="file"
                        accept={ATTACHMENT_ALLOWED_MIME_TYPES.join(',')}
                        style={{ display: 'none' }}
                        disabled={busy}
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) handleUploadAttachment(type, number, file);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>
              );
            })}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="submit" className="btn btn-primary" disabled={savingReminder}>
                {savingReminder ? t('auth.loading') : t('emailteksten.opslaan')}
              </button>
            </div>
          </form>
        )}
      </>
    );
  }

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const allKeys = isSuperadmin ? [...ALL_TEKST_KEYS, ...HTML_KEYS] : ALL_TEKST_KEYS;
        const { data, error } = await supabase
          .from('settings')
          .select('key, value')
          .in('key', allKeys);
        if (error) throw error;
        const map = {};
        (data || []).forEach(({ key, value }) => { map[key] = value ?? ''; });
        setTeksten(prev => ({ ...prev, ...Object.fromEntries(ALL_TEKST_KEYS.map(k => [k, map[k] ?? prev[k]])) }));
        if (isSuperadmin) {
          setHtmlTemplates(prev => ({ ...prev, ...Object.fromEntries(HTML_KEYS.map(k => [k, map[k] ?? prev[k]])) }));
        }
      } catch (err) {
        showError(t('emailteksten.foutLaden') + err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [isSuperadmin]);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const currentTab = MAIL_TYPES.find(t => t.id === activeTab);
      const keysToSave = currentTab ? currentTab.keys : [];
      const rows = keysToSave.map(key => ({ key, value: teksten[key] ?? '' }));
      if (!rows.length) return;
      const { data: saved, error } = await supabase
        .from('settings')
        .upsert(rows, { onConflict: 'key' })
        .select('key');
      if (error) throw error;
      if ((saved?.length ?? 0) !== rows.length) {
        showError(t('common.wijzigingNietOpgeslagen'));
        return;
      }
      showSuccess(t('emailteksten.opgeslagen'));
    } catch (err) {
      showError(t('emailteksten.foutOpslaanTeksten') + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveHtml(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const activeHtmlKey = activeTab.replace('html_', 'email_html_');
      const key = activeTab.startsWith('html_') ? activeHtmlKey : null;
      if (!key) return;
      const { data: savedRow, error } = await supabase
        .from('settings')
        .upsert([{ key, value: htmlTemplates[key] ?? '' }], { onConflict: 'key' })
        .select('key')
        .maybeSingle();
      if (error) throw error;
      if (!savedRow) {
        showError(t('common.wijzigingNietOpgeslagen'));
        return;
      }
      showSuccess(t('emailteksten.opgeslagen'));
    } catch (err) {
      showError(t('emailteksten.foutOpslaanHtml') + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAllTeksten(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const rows = ALL_TEKST_KEYS.map(key => ({ key, value: teksten[key] ?? '' }));
      const { data: saved, error } = await supabase
        .from('settings')
        .upsert(rows, { onConflict: 'key' })
        .select('key');
      if (error) throw error;
      if ((saved?.length ?? 0) !== rows.length) {
        showError(t('common.wijzigingNietOpgeslagen'));
        return;
      }
      showSuccess(t('emailteksten.opslagenTeksten'));
    } catch (err) {
      showError(t('emailteksten.foutOpslaanTeksten') + err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  const isHtmlTab = activeTab.startsWith('html_');
  const currentMailType = MAIL_TYPES.find(mt => mt.id === activeTab);

  const allTabs = [
    ...MAIL_TYPES,
    { id: 'agreement_reminder', label: t('emailteksten.tabOvereenkomstHerinnering') },
    { id: 'prospect_reminder', label: t('emailteksten.tabProspectHerinnering') },
    ...(isSuperadmin ? HTML_TABS.map(h => ({ id: 'html_' + h.key, label: h.label, htmlKey: h.key })) : []),
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ marginBottom: 6 }}>
            <Link to="/beheer/organisatie/automatisering" className="text-muted" style={{ fontSize: 13 }}>
              ← {t('beheer.organisatieTitel')}
            </Link>
          </div>
          <h1 className="page-title">{t('emailteksten.title')}</h1>
          <p className="page-subtitle">{t('emailteksten.subtitle')}</p>
        </div>
      </div>

      <nav className="communication-subnav" aria-label={t('beheer.communicatieNavigatie')}>
        <Link className="is-active" to="/beheer/communicatie/email">{t('beheer.emailteksten')}</Link>
        <Link to="/beheer/communicatie/facturen">{t('beheer.factuurteksten')}</Link>
      </nav>

      <div className="communication-workbench">
      {/* Sjabloonnavigatie */}
      <nav className="template-nav" aria-label={t('emailteksten.title')}>
        {allTabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={activeTab === tab.id ? 'is-active' : ''}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Tab content */}
      <div className="template-editor">
      <div className="card">
        {/* Algemeen tab */}
        {activeTab === 'algemeen' && (
          <>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, marginTop: 0 }}>
              {t('emailteksten.tabAlgemeen')}
            </h2>
            <p className="text-muted" style={{ fontSize: 13, marginBottom: 24 }}>
              {t('emailteksten.tabAlgemeenSubtitel')}
            </p>
            <form onSubmit={handleSave}>
              {['email_aanhef', 'email_slotgroet'].map(key => {
                const meta = FIELD_META[key];
                return (
                  <div className="form-group" key={key}>
                    <label className="form-label">{meta.label}</label>
                    {meta.rows === 1 ? (
                      <input
                        className="form-input"
                        value={teksten[key]}
                        onChange={e => setTeksten(p => ({ ...p, [key]: e.target.value }))}
                        placeholder={meta.placeholder}
                      />
                    ) : (
                      <textarea
                        className="form-input"
                        rows={meta.rows}
                        value={teksten[key]}
                        onChange={e => setTeksten(p => ({ ...p, [key]: e.target.value }))}
                        placeholder={meta.placeholder}
                        style={{ resize: 'vertical' }}
                      />
                    )}
                    {meta.hint && (
                      <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>{meta.hint}</p>
                    )}
                  </div>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? t('auth.loading') : t('emailteksten.opslaan')}
                </button>
              </div>
            </form>
          </>
        )}

        {/* Mailtypes tabs (factuur, herinnering, aanmaning, incasso, welcome_*) */}
        {currentMailType && activeTab !== 'algemeen' && (
          <>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, marginTop: 0 }}>
              {currentMailType.label}
            </h2>
            <p className="text-muted" style={{ fontSize: 13, marginBottom: 20 }}>
              {t('emailteksten.tabMailtypeSubtitel')}
            </p>
            <VariabelenChips variabelen={currentMailType.variabelen} />
            <form onSubmit={handleSave}>
              {currentMailType.keys.map(key => {
                const meta = FIELD_META[key] || { label: key, rows: 8, placeholder: '' };
                return (
                  <div className="form-group" key={key}>
                    <label className="form-label">{meta.label}</label>
                    {meta.rows === 1 ? (
                      <input
                        className="form-input"
                        value={teksten[key]}
                        onChange={e => setTeksten(p => ({ ...p, [key]: e.target.value }))}
                        placeholder={meta.placeholder}
                      />
                    ) : (
                      <textarea
                        className="form-input"
                        rows={meta.rows}
                        value={teksten[key]}
                        onChange={e => setTeksten(p => ({ ...p, [key]: e.target.value }))}
                        placeholder={meta.placeholder}
                        style={{ resize: 'vertical', minHeight: meta.rows * 24 }}
                      />
                    )}
                    {meta.hint && (
                      <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>{meta.hint}</p>
                    )}
                  </div>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? t('auth.loading') : t('emailteksten.opslaan')}
                </button>
              </div>
            </form>
          </>
        )}

        {/* Overeenkomst-/prospect-herinneringen (per-index teksten + bijlagen) */}
        {activeTab === 'agreement_reminder' && renderReminderTab('agreement')}
        {activeTab === 'prospect_reminder' && renderReminderTab('prospect')}

        {/* HTML template tabs (superadmin only) */}
        {isHtmlTab && (() => {
          const htmlTabMeta = HTML_TABS.find(h => 'html_' + h.key === activeTab);
          const htmlKey = htmlTabMeta?.key;
          return htmlKey ? (
            <>
              <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, marginTop: 0 }}>
                {htmlTabMeta.label}
              </h2>
              <div style={{
                background: '#fff3e0',
                border: '1px solid #ffb74d',
                borderRadius: 8,
                padding: 16,
                marginBottom: 20,
              }}>
                <p style={{ fontWeight: 600, fontSize: 13, margin: '0 0 6px', color: '#e65100' }}>
                  {t('emailteksten.waarschuwingTitel')}
                </p>
                <p style={{ fontSize: 12, color: '#795548', margin: 0, lineHeight: 1.5 }}>
                  {t('emailteksten.waarschuwingTekst')}
                </p>
              </div>
              <form onSubmit={handleSaveHtml}>
                <div className="form-group">
                  <label className="form-label">{htmlTabMeta.label}</label>
                  <textarea
                    className="form-input"
                    rows={16}
                    value={htmlTemplates[htmlKey]}
                    onChange={e => setHtmlTemplates(p => ({ ...p, [htmlKey]: e.target.value }))}
                    placeholder="<!DOCTYPE html>..."
                    style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? t('auth.loading') : t('emailteksten.opslaan')}
                  </button>
                </div>
              </form>
            </>
          ) : null;
        })()}
      </div>
      <EmailPreview activeTab={activeTab} teksten={teksten} />
      </div>
      </div>
    </div>
  );
}
